import { Copy, Export } from '@phosphor-icons/react'
import cx from 'classnames'
import { TFunction } from 'i18next'
import { buttonText, errorT } from '../../../../../utils/ec-flow-texts'
import { useState } from 'react'
import { NavigateFunction } from 'react-router-dom'
import i18n from '../../../../../../../client/src/i18n'
import {
  EndUserCampaign,
  Integration,
  PartialEndUser,
} from '../../../../../lib/api/types'
import { endUserType } from '../../../../../utils/user-type'
import { hasHappinessCheck } from '../../../../../utils/happiness-check'
import { Button } from '@alphabait/ui'
import { useBrand } from '../../../../../contexts/brand-context'
import Header from '../../../../header'

export default function Referral({
  campaign,
  endUser,
  id,
  t,
  pathname,
  navigate,
  integrations,
}: {
  integrations: Integration[]
  campaign: EndUserCampaign
  endUser: PartialEndUser
  t: TFunction<'translation', undefined>
  pathname: string
  id?: string
  navigate: NavigateFunction
}) {
  const { brand } = useBrand()
  const [showCopyToClipboardTooltip, setShowCopyToClipboardTooltip] =
    useState<boolean>(false)

  if (endUser?.referralOf && !campaign?.referralCampaign) {
    return <p>404</p>
  }

  const referralLink =
    (campaign.endUserUrl || import.meta.env.VITE_PUBLIC_URL) +
    '/' +
    endUser.id +
    '/referral'

  const isHeardFoundation = integrations?.some(
    (integration) => integration.features?.['template'] === 'heart-foundation',
  )

  // A brand-specific key (ec.invite.share_msg_<brand>) wins over the customer's
  // heart-foundation text, which wins over the generic message.
  // The share message follows the campaign's error language (REQ-0005).
  const shareT = errorT(campaign)
  const brandShareKey = `ec.invite.share_msg_${brand}`
  const shareMessage = i18n.exists(brandShareKey)
    ? shareT(brandShareKey)
    : isHeardFoundation
      ? shareT('ec.invite.share_msg_heart_foundation')
      : shareT('ec.invite.share_msg')

  const shareOptions = {
    title: shareMessage,
    text: shareMessage,
    url: referralLink,
  }
  const language = i18n.language
  const typeOfUser = endUserType(endUser.referral)
  const translations =
    campaign?.ecFlowTexts?.ecFlowTexts?.[typeOfUser]?.['invite']?.[language]
  const handleCopyToClipboard = () => {
    window.navigator.clipboard.writeText(referralLink)
    setShowCopyToClipboardTooltip(true)
    setTimeout(() => {
      setShowCopyToClipboardTooltip(false)
    }, 2000)
  }
  return (
    <div className={cx('flex flex-col w-full bg-background')}>
      <Header
        title={
          translations?.header ?? t('ec.invite.title')
        }
        subTitle={
          translations?.text ?? t('ec.invite.sub_title')
        }
        pathname={pathname}
      />

      <div className="m-auto bg-background grid gap-y-2 p-4 m-auto max-w-md px-6">
        <div className="border border-base-input bg-background-light rounded-md p-4">
          <div className="flex relative justify-between rounded-lg text-slate-50 text-xl font-bold m-auto mx-4 ">
            <span className="self-center text-xs">{referralLink}</span>
          </div>
          <div className="flex flex-row justify-center pt-3.5 gap-6 text-slate-50 text-xs">
            <div className="flex flex-col items-center">
              <div className="p-2 rounded-full items-center cursor-pointer">
                <Copy
                  size={28}
                  weight="fill"
                  onClick={handleCopyToClipboard}
                  color="var(--color-slate-50)"
                />
                {showCopyToClipboardTooltip && (
                  <span className="bg-background border border-base-input text-slate-50 text-center whitespace-nowrap absolute rounded-lg p-2 transform -translate-y-12">
                    {buttonText(campaign, 'copied', t)}
                  </span>
                )}
              </div>
              <p className="pt-1">{buttonText(campaign, 'copy', t)}</p>
            </div>
            {window.navigator?.canShare?.(shareOptions) && (
              <div className="flex flex-col items-center">
                <div className="p-2 rounded-full cursor-pointer">
                  <Export
                    size={28}
                    onClick={() => window.navigator.share(shareOptions)}
                    color="var(--color-slate-50)"
                  />
                </div>
                <p className="pt-1">{buttonText(campaign, 'share', t)}</p>
              </div>
            )}
          </div>
        </div>
        <div className="grid gap-4 my-4 py-4 max-w-md mx-auto w-full mt-10">
          {hasHappinessCheck(campaign) ? (
            <Button
              variant="next"
              size="lg"
              onClick={() => navigate(`/${id}/happiness-check`)}
              data-testid="next-button"
            >
              {buttonText(campaign, 'next', t)}
            </Button>
          ) : (
            <Button
              variant="next"
              size="lg"
              onClick={() => navigate(`/${id}/success`)}
              data-testid="next-button"
            >
              {buttonText(campaign, 'finish', t)}
            </Button>
          )}
          <Button
            variant="back"
            size="lg"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            {buttonText(campaign, 'back', t)}
          </Button>
        </div>
      </div>
    </div>
  )
}
